<template>
  <LeadsList
    :leads="leads"
    :facets="facets"
    :pagination="pagination"
    :selected-type="selectedType"
    :archived="archived"
    :statuses="statuses"
    :context="context"
    :contactable-name="contactableName"
    :loading="loading"
    :exporting="exporting"
    @update:selected-type="updateSelectedType"
    @update:archived="updateArchived"
    @update:statuses="updateStatuses"
    @update:page="updatePage"
    @update:per-page="updatePerPage"
    @export="downloadCsv"
  />
</template>

<script setup lang="ts">
import { track } from '@ramp106/omrjs-tracking-reviews'
import { useApolloClient } from '@vue/apollo-composable'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import { useRouterQueryPagination } from '@/composables/useRouterQueryPagination'
import {
  ContactRequestContactRequestTypeEnum,
  LeadsContactRequestTypeEnum,
  LeadsSortByEnum,
  ManageLeadsFieldKeyEnum,
  LeadStatusEnum,
  ReviewsLeadsExportCsvDocument,
  type ReviewsLeadsExportCsvQuery,
  SortDirectionEnum,
  useReviewsLeadsQuery,
} from '@/gql/reviews'
import { showNotification } from '@/helpers/notificationHelper'

import LeadsList from './LeadsList.vue'
import type { LeadsContext } from './types'

const props = defineProps<{
  contactableId: string
  contactableName: string
  context: LeadsContext
}>()

const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const { page, perPage, updatePage, updatePerPage } = useRouterQueryPagination()

const selectedType = computed<LeadsContactRequestTypeEnum>(() => {
  const type = route.query.type as LeadsContactRequestTypeEnum
  const agencyHiddenTypes = [LeadsContactRequestTypeEnum.CustomQuote, LeadsContactRequestTypeEnum.ReviewStarter]
  if (props.context === 'agency' && agencyHiddenTypes.includes(type)) return LeadsContactRequestTypeEnum.All
  return Object.values(LeadsContactRequestTypeEnum).includes(type) ? type : LeadsContactRequestTypeEnum.All
})

function updateSelectedType(type: LeadsContactRequestTypeEnum) {
  router.push({
    query: {
      ...route.query,
      page: undefined,
      type: type === LeadsContactRequestTypeEnum.All ? undefined : type,
    },
  })
}

const archived = computed<boolean>(() => route.query.archived === 'true')

function updateArchived(value: boolean) {
  router.push({
    query: {
      ...route.query,
      page: undefined,
      archived: value ? 'true' : undefined,
    },
  })
}

const statuses = computed<LeadStatusEnum[]>(() =>
  String(route.query.status ?? '')
    .split(',')
    .filter((value): value is LeadStatusEnum => Object.values(LeadStatusEnum).includes(value as LeadStatusEnum)),
)

function updateStatuses(value: LeadStatusEnum[]) {
  router.push({
    query: {
      ...route.query,
      page: undefined,
      status: value.length ? value.join(',') : undefined,
    },
  })
}

const contactRequestTypeFilter = computed<ContactRequestContactRequestTypeEnum[] | undefined>(() => {
  switch (selectedType.value) {
    case LeadsContactRequestTypeEnum.ContactForm:
      return [ContactRequestContactRequestTypeEnum.ContactForm]
    case LeadsContactRequestTypeEnum.CustomQuote:
      return [ContactRequestContactRequestTypeEnum.CustomQuote]
    case LeadsContactRequestTypeEnum.FileDownload:
      return [ContactRequestContactRequestTypeEnum.FileDownload]
    case LeadsContactRequestTypeEnum.ReviewStarter:
      return [ContactRequestContactRequestTypeEnum.ReviewStarter]
    default:
      return undefined
  }
})

const variables = computed(() => ({
  contactableId: props.contactableId,
  page: page.value,
  perPage: perPage.value,
  sortBy: LeadsSortByEnum.CreatedAt,
  sortDirection: SortDirectionEnum.Desc,
  filters: {
    contactRequestType: contactRequestTypeFilter.value,
    archived: archived.value,
    status: statuses.value.length ? statuses.value : undefined,
  },
}))

// keepPreviousResult: without it, result resets while refetching on tab/page
// changes, flashing zeroed facet counts and an empty list
// cache-and-network: archiving happens on the detail route, so a cache-first
// remount would serve a list still containing the archived lead
const { loading, result } = useReviewsLeadsQuery(variables, {
  clientId: 'reviews',
  keepPreviousResult: true,
  fetchPolicy: 'cache-and-network',
})

const leads = computed(() => result.value?.manage?.leads?.results ?? [])
const facets = computed(() => result.value?.manage?.leads?.contactRequestTypeFacet ?? [])

const pagination = computed(() => ({
  page: result.value?.manage?.leads?.pagination.currentPage ?? page.value,
  perPage: result.value?.manage?.leads?.pagination.perPage ?? perPage.value,
  total: result.value?.manage?.leads?.pagination.totalCount ?? 0,
}))

// Array order is the CSV column order — the backend applies none of its own.
const exportFields = computed(() => [
  { key: ManageLeadsFieldKeyEnum.CreatedAt, name: t('Leads.table.receivedAt') },
  { key: ManageLeadsFieldKeyEnum.ContactRequestType, name: t('Leads.table.type') },
  { key: ManageLeadsFieldKeyEnum.SenderName, name: t('Leads.table.name') },
  { key: ManageLeadsFieldKeyEnum.SenderCompany, name: t('Leads.table.company') },
  { key: ManageLeadsFieldKeyEnum.SenderCompanyPosition, name: t('Leads.details.position') },
  { key: ManageLeadsFieldKeyEnum.SenderEmail, name: t('Leads.table.email') },
  { key: ManageLeadsFieldKeyEnum.SenderPhone, name: t('Leads.details.phone') },
  { key: ManageLeadsFieldKeyEnum.Message, name: t('Leads.details.message') },
])

// No pagination: the export covers every page, not the one on screen.
const exportVariables = computed(() => ({
  contactableId: props.contactableId,
  fields: exportFields.value,
  filters: {
    contactRequestType: contactRequestTypeFilter.value,
    archived: archived.value,
    status: statuses.value.length ? statuses.value : undefined,
  },
  sortBy: LeadsSortByEnum.CreatedAt,
  sortDirection: SortDirectionEnum.Desc,
}))

const { resolveClient } = useApolloClient()
const exporting = ref(false)

async function downloadCsv() {
  track('export_download', {
    surface: 'dashboard',
    entity_type: props.context === 'agency' ? 'service' : 'tool',
    entity_id: props.contactableId,
    export_type: 'leads',
  })

  exporting.value = true
  try {
    // Not useLazyQuery: its reactive variables binding re-exports on every filter change.
    const { data } = await resolveClient('reviews').query<ReviewsLeadsExportCsvQuery>({
      query: ReviewsLeadsExportCsvDocument,
      variables: exportVariables.value,
      fetchPolicy: 'no-cache',
    })
    const csv = data?.manage?.leadsExportCsv

    if (csv?.errors?.length || !csv?.fileUrl) {
      showNotification(t('Leads.export.errorNotification'), 'error')
      return
    }

    window.open(csv.fileUrl, '_blank')
  } catch {
    showNotification(t('Leads.export.errorNotification'), 'error')
  } finally {
    exporting.value = false
  }
}
</script>
